import React, { createContext, useState } from 'react';
import { NAVIGATION_ENDPOINTS } from '../nav/constants/urls';

type ConversationTopic = {
  id: string;
  topic: string;
};
type Conversation = {
  role: string;
  content: string;
};

type Feedback = {
  rate: number;
  comments: string;
} | null;
interface Incident {
  updated_at: string;
}
type IConversationTopic = ConversationTopic | null;
type SupportContextProps = {
  question: string;
  setQuestion: (question: string) => void;
  conversationsList: Array<ConversationTopic>;
  setConversationsList: (conversationsList: Array<ConversationTopic>) => void;
  currentConversation: IConversationTopic;
  setCurrentConversation: (currentConversation: ConversationTopic) => void;
  conversationHistory: Array<Conversation>;
  setConversationHistory: (conversation: Array<Conversation>) => void;
  openAbout: boolean;
  toggleOpenAbout: (openAbout: boolean) => void;
  isBotAnswering: boolean;
  setIsBotAnswering: (isBotAnswering: boolean) => void;
  openMonthlyQuota: boolean;
  toggleOpenMonthlyQuota: (openMonthlyQuota: boolean) => void;
  quotaOver: boolean;
  setQuotaOver: (quotaOver: boolean) => void;
  timeoutError: boolean;
  setTimeoutError: (timeoutError: boolean) => void;
  toggleBot: boolean;
  setToggleBot: (toggleBot: boolean) => void;
  handleUserRequest: () => void;
  updateLatestConversation: () => void;
  getResponse: (isTryAgain: boolean) => void;
  chatBotUnavailable: boolean;
  setChatBotUnavailable: (chatBotUnavailable: boolean) => void;
  alertMessageCancel: boolean;
  setAlertMessageReadCancel: (alertMessageCancel: boolean) => void;
  currentArticleConversation: Conversation | null;
  setCurrentArticleConversation: (
    currentArticleConversation: Conversation | null,
  ) => void;
  chatBotFeedback: Array<Feedback>;
  setChatBotFeedback: (chatBotFeedback: Array<Feedback>) => void;
};
export const SupportContext = createContext<SupportContextProps | null>(null);
const SupportProvider = (props: any) => {
  const [question, setQuestion] = useState('');
  const [conversationsList, setConversationsList] = useState<
    Array<ConversationTopic>
  >([]);
  const [currentConversation, setCurrentConversation] =
    useState<IConversationTopic>(null);
  const [currentArticleConversation, setCurrentArticleConversation] =
    useState<Conversation | null>(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [openAbout, toggleOpenAbout] = useState(false);
  const [isBotAnswering, setIsBotAnswering] = useState(false);
  const [openMonthlyQuota, toggleOpenMonthlyQuota] = useState(false);
  const [quotaOver, setQuotaOver] = useState(false);
  const [timeoutError, setTimeoutError] = useState(false);
  const [toggleBot, setToggleBot] = useState(false);
  const [chatBotUnavailable, setChatBotUnavailable] = useState(false);
  const [alertMessageCancel, setAlertMessageReadCancel] = useState(false);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [chatBotFeedback, setChatBotFeedback] = useState<Array<Feedback>>([]);

  const { children } = props;
  const QUOTA_OVER_ERROR_CODE = 429;
  const TIMED_OUT_ERROR_CODE = 408;
  const CHAT_BOT_UNAVAILABLE = [500, 503, 504];

  const startConversation = async (searchText?: string) => {
    try {
      let conversation = {
        role: 'user',
        content: question || searchText,
      };
      setConversationHistory((prev) => [...prev, conversation]);
      setQuestion('');
      const tempQuestion = question || searchText;
      setIsBotAnswering(true);
      const response = await fetch(
        NAVIGATION_ENDPOINTS.COPILOT_SUPPORT_CONVERSATIONS,
        {
          method: 'POST',
          body: JSON.stringify({ message: question || searchText }),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );
      const { status } = response;
      if (status === QUOTA_OVER_ERROR_CODE) {
        setQuotaOver(true);
      } else if (status === TIMED_OUT_ERROR_CODE) {
        setTimeoutError(true);
      } else if (CHAT_BOT_UNAVAILABLE.includes(status)) {
        setChatBotUnavailable(true);
      } else {
        const reply = await response.text();
        const id = response.headers.get('Conversation-Id');
        conversation = {
          role: 'assistant',
          content: reply,
        };
        setConversationsList((prev) => [{ id, topic: tempQuestion }, ...prev]);
        setCurrentConversation({ id, topic: tempQuestion });
        setConversationHistory((prev) => [...prev, conversation]);
        if (searchText) setCurrentArticleConversation(conversation);
        setQuotaOver(false);
        setTimeoutError(false);
        setChatBotUnavailable(false);
      }
    } catch (e) {
      console.log(e);
    }
    setIsBotAnswering(false);
  };

  const getResponse = async (isTryAgain = false) => {
    try {
      let conversation = {
        role: 'user',
        content: question,
      };
      if (!isTryAgain) {
        setConversationHistory((prev) => [...prev, conversation]);
      }
      setQuestion('');
      setIsBotAnswering(true);
      const response = await fetch(
        `${NAVIGATION_ENDPOINTS.COPILOT_SUPPORT_CONVERSATIONS}/${currentConversation?.id}`,
        {
          method: 'POST',
          body: JSON.stringify({ message: question }),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );
      const { status } = response;
      if (status === QUOTA_OVER_ERROR_CODE) {
        setQuotaOver(true);
      } else if (status === TIMED_OUT_ERROR_CODE) {
        setTimeoutError(true);
      } else if (CHAT_BOT_UNAVAILABLE.includes(status)) {
        setChatBotUnavailable(true);
      } else {
        const reply = await response.text();
        conversation = {
          role: 'assistant',
          content: reply,
        };
        setConversationHistory((prev) => [...prev, conversation]);
        setTimeoutError(false);
        setQuotaOver(false);
        setChatBotUnavailable(false);
      }
    } catch (e) {
      console.log(e);
    }
    setIsBotAnswering(false);
  };

  const handleUserRequest = async (searchText?: string) => {
    if (searchText) {
      startConversation(searchText);
    } else {
      if (question) {
        if (!conversationHistory.length || !currentConversation?.id) {
          startConversation();
        } else if (currentConversation?.id) {
          getResponse();
        }
      }
    }
  };
  const getIncidents = async (query = ''): Promise<void> => {
    const endpoint = query
      ? `${NAVIGATION_ENDPOINTS.TRUST_INCIDENTS}?searchParam=${query}`
      : `${NAVIGATION_ENDPOINTS.TRUST_INCIDENTS}`;
    try {
      const response = await fetch(endpoint);

      if (!response.ok) {
        console.error(`Error fetching incidents: ${response.statusText}`);
        return;
      }

      const incidents: Incident[] = await response.json();
      const currentDate = new Date();

      const recentIncidents = incidents.slice(0, 5).filter((incident) => {
        const ticketUpdatedDate = new Date(incident.updated_at);
        const hoursElapsed =
          (currentDate.getTime() - ticketUpdatedDate.getTime()) /
          (1000 * 60 * 60);
        return hoursElapsed < 48;
      });

      setIncidents(recentIncidents);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    }
  };

  const updateLatestConversation = () => {
    const latestConversation = conversationHistory.splice(-1);
    setConversationHistory(latestConversation);
  };

  const handleFeedbackSubmission = async () => {
    const topFeedback = chatBotFeedback[0];
    try {
      const response = await fetch(
        `${NAVIGATION_ENDPOINTS.COPILOT_SUPPORT_FEEDBACK}/${currentConversation?.id}/feedback`,
        {
          method: 'PUT',
          body: JSON.stringify(topFeedback),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.ok) {
        console.log('Feedback submitted successfully');
      } else {
        console.error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  return (
    <SupportContext.Provider
      value={{
        question,
        setQuestion,
        conversationsList,
        setConversationsList,
        currentConversation,
        setCurrentConversation,
        conversationHistory,
        setConversationHistory,
        handleUserRequest,
        isBotAnswering,
        setIsBotAnswering,
        openAbout,
        toggleOpenAbout,
        openMonthlyQuota,
        toggleOpenMonthlyQuota,
        quotaOver,
        setQuotaOver,
        toggleBot,
        setToggleBot,
        timeoutError,
        setTimeoutError,
        getResponse,
        chatBotUnavailable,
        setChatBotUnavailable,
        alertMessageCancel,
        setAlertMessageReadCancel,
        currentArticleConversation,
        setCurrentArticleConversation,
        updateLatestConversation,
        incidents,
        getIncidents,
        chatBotFeedback,
        setChatBotFeedback,
        handleFeedbackSubmission,
      }}
    >
      {children}
    </SupportContext.Provider>
  );
};

export default SupportProvider;
